<script setup lang="ts">
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { UiText, UiIcon, UiCheckbox, UiLockedBadge, UiAvatar, PartnerChip } from '@shopexperts/shared';
import type { AdminExpertListItem, ExpertProfileStatus, PartnerBadge } from '@shopexperts/legacy-api';
import { getExpertTypeAppearance } from './expertTypeAppearance';

function getBadgeIconUrl(badge: PartnerBadge): string | undefined {
  return badge.icon?.cdn_url || badge.icon?.url || undefined;
}

const props = defineProps<{
  expert: AdminExpertListItem;
  isActionLoading?: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  selectionDisabled?: boolean;
}>();

const emit = defineEmits<{
  click: [];
  toggleSelection: [rangeSelect: boolean];
  loginAs: [];
  statusAction: [action: 'activate' | 'publish' | 'unpublish' | 'setDraft' | 'setApplication' | 'deleteApplication'];
  resendActivation: [];
  switchType: [];
  reassignLevel: [];
  sendInvite: [];
}>();

let rangeSelectHeld = false;
function captureRangeSelect(event: MouseEvent | KeyboardEvent) {
  rangeSelectHeld = event.shiftKey;
}
const detailHref = computed(() => `/admin/expert-manager/${props.expert.id}`);

function handleCardClick(event: MouseEvent) {
  if (event.metaKey || event.ctrlKey || event.shiftKey && !props.selectionMode || event.button === 1) return;
  event.preventDefault();
  if (props.selectionMode) {
    if (!props.selectionDisabled) emit('toggleSelection', event.shiftKey);
  } else emit('click');
}

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

onClickOutside(menuRef, () => { menuOpen.value = false; });

function toggleMenu(e: MouseEvent) {
  e.stopPropagation();
  menuOpen.value = !menuOpen.value;
}

function handleAction(action: string) {
  menuOpen.value = false;
  if (action === 'loginAs') {
    emit('loginAs');
  } else if (action === 'resendActivation') {
    emit('resendActivation');
  } else if (action === 'switchType') {
    emit('switchType');
  } else if (action === 'reassignLevel') {
    emit('reassignLevel');
  } else if (action === 'sendInvite') {
    emit('sendInvite');
  } else {
    emit('statusAction', action as any);
  }
}

const statusClasses = computed(() => {
  const map: Record<ExpertProfileStatus, string> = {
    published: 'text-emerald-700 bg-emerald-50',
    application: 'text-amber-700 bg-amber-50',
    draft: 'text-gray-500 bg-gray-50',
    unpublished: 'text-rose-600 bg-rose-50',
    banned: 'text-gray-700 bg-gray-100',
  };
  return map[props.expert.profile_status] ?? 'text-gray-500 bg-gray-50';
});

const tierDisplay = computed(() => {
  if (!props.expert.partner_tier || props.expert.partner_tier === 'none') return null;
  return props.expert.partner_tier.charAt(0).toUpperCase() + props.expert.partner_tier.slice(1);
});

const locationLabel = computed(() => [props.expert.city?.name, props.expert.country].filter(Boolean).join(', '));

const priceDisplay = computed(() => {
  if (!props.expert.starting_price) return null;
  return `$${props.expert.starting_price.toLocaleString()}`;
});

const typeDisplay = computed(() => {
  return getExpertTypeAppearance(props.expert.listing_type).label;
});

const avatarRingClass = computed(() => {
  return getExpertTypeAppearance(props.expert.listing_type).avatarRingClass;
});

const typeTextClass = computed(() => {
  return getExpertTypeAppearance(props.expert.listing_type).typeTextClass;
});

const joinedDate = computed(() => {
  if (!props.expert.created_at) return '';
  return new Date(props.expert.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
});

interface MenuItem {
  key: string;
  label: string;
  icon: string;
  variant?: 'default' | 'danger';
}

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [];
  const s = props.expert.profile_status;

  if (s === 'application') {
    items.push({ key: 'activate', label: 'Activate Application', icon: 'check', variant: 'default' });
    items.push({ key: 'deleteApplication', label: 'Delete Application', icon: 'close', variant: 'danger' });
  }
  if (s === 'draft' || s === 'unpublished') {
    items.push({ key: 'publish', label: 'Publish', icon: 'check-circle', variant: 'default' });
  }
  if (s === 'published') {
    items.push({ key: 'unpublish', label: 'Unpublish', icon: 'close', variant: 'danger' });
  }
  if (s === 'published' || s === 'unpublished') {
    items.push({ key: 'setDraft', label: 'Set to Draft', icon: 'pencil', variant: 'default' });
  }
  if (s === 'draft') {
    items.push({ key: 'setApplication', label: 'Set to Application', icon: 'arrow-send', variant: 'default' });
    items.push({ key: 'resendActivation', label: 'Resend Activation Email', icon: 'arrow-send', variant: 'default' });
  }
  if (props.expert.is_claimed === false) {
    items.push({ key: 'sendInvite', label: 'Send Invite Email', icon: 'arrow-send', variant: 'default' });
  }

  items.push({ key: 'reassignLevel', label: 'Reassign Level', icon: 'sparkle', variant: 'default' });
  items.push({ key: 'switchType', label: 'Switch Type', icon: 'arrow-send', variant: 'default' });
  items.push({ key: 'loginAs', label: 'Login As Expert', icon: 'link-external', variant: 'default' });

  return items;
});
</script>

<template>
  <article
    class="group cursor-pointer relative"
    :class="{
      'opacity-60': expert.profile_status === 'banned',
      'z-20': menuOpen,
      'select-none': selectionMode,
    }"
  >
    <a :href="detailHref" class="absolute inset-0 z-0 rounded-lg" :aria-label="expert.display_name" @click="handleCardClick" />
    <div
      class="flex border rounded-lg overflow-visible transition-all duration-200"
      :class="selected && selectionMode
        ? 'bg-se-primary/10 border-se-primary/40'
        : 'bg-white border-gray-100 group-hover:shadow-md group-hover:border-gray-200'"
    >
      <!-- Left accent strip -->
      <div
        class="w-1 shrink-0 self-stretch rounded-l-lg"
        :class="{
          'bg-emerald-400': expert.profile_status === 'published',
          'bg-amber-400': expert.profile_status === 'application',
          'bg-gray-300': expert.profile_status === 'draft',
          'bg-rose-400': expert.profile_status === 'unpublished',
          'bg-gray-700': expert.profile_status === 'banned',
        }"
      />

      <div class="flex-1 min-w-0 p-5">
        <div class="flex items-start gap-4">
          <UiCheckbox
            v-if="selectionMode"
            :model-value="selected"
            :disabled="selectionDisabled"
            :aria-label="`Select ${expert.display_name}`"
            class="relative z-10 shrink-0 mt-2"
            @mousedown="captureRangeSelect"
            @keydown="captureRangeSelect"
            @click.stop
            @update:model-value="emit('toggleSelection', rangeSelectHeld)"
          />
          <UiAvatar
            :name="expert.display_name"
            :url="expert.avatar ?? undefined"
            size="xs"
            :responsive="false"
            color-coded
            class="shrink-0 ring-2 ring-offset-2 ring-offset-white"
            :class="avatarRingClass"
          />

          <div class="flex-1 min-w-0 flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2.5">
                <UiText
                  as="h3"
                  variant="h5"
                  :text="expert.display_name"
                  class="font-semibold text-gray-900"
                  :truncate="1"
                />
                <UiLockedBadge v-if="expert.is_pro" />
              </div>

              <div class="flex items-center gap-1.5 mt-0.5">
                <UiText as="span" variant="caption-normal" :class="typeTextClass">
                  {{ typeDisplay }}
                </UiText>
                <template v-if="tierDisplay">
                  <span class="text-gray-300">·</span>
                  <UiText as="span" variant="caption-normal" class="text-se-highlight font-medium">
                    {{ tierDisplay }}
                  </UiText>
                </template>
                <template v-if="expert.email">
                  <span class="text-gray-300">·</span>
                  <UiText as="span" variant="caption-normal" class="text-gray-500 truncate min-w-0">
                    {{ expert.email }}
                  </UiText>
                </template>
                <template v-if="locationLabel">
                  <span class="text-gray-300">·</span>
                  <UiText as="span" variant="caption-normal" class="text-gray-500 whitespace-nowrap">
                    {{ locationLabel }}
                  </UiText>
                </template>
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-1.5 pl-2">
              <UiText
                as="span"
                variant="caption"
                class="tracking-wide px-2 py-0.5 rounded shrink-0"
                :class="statusClasses"
              >
                {{ expert.profile_status }}
              </UiText>

              <div
                v-if="!selectionMode"
                ref="menuRef"
                class="relative z-10"
                @click.stop
              >
                <button
                  @click="toggleMenu"
                  class="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                  :class="{ 'bg-gray-100 text-gray-700!': menuOpen }"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>

                <Transition
                  enter-active-class="transition duration-100 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition duration-75 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <div
                    v-if="menuOpen"
                    class="absolute right-0 top-full mt-1 w-52 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-50"
                  >
                    <template v-for="(item, idx) in menuItems" :key="item.key">
                      <div
                        v-if="item.key === 'loginAs' && idx > 0"
                        class="my-1 border-t border-gray-100"
                      />
                      <button
                        class="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                        :class="item.variant === 'danger'
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-gray-700 hover:bg-gray-50'
                        "
                        :disabled="isActionLoading"
                        @click="handleAction(item.key)"
                      >
                        <UiIcon :name="(item.icon as any)" class="w-4 h-4 shrink-0 opacity-60" />
                        <UiText as="span" variant="p-small">{{ item.label }}</UiText>
                      </button>
                    </template>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Detail rows — indented past avatar -->
        <div class="pl-16">
          <!-- Stats -->
          <div class="flex items-center gap-4 mt-2.5 flex-wrap">
            <div v-if="expert.reviews_count > 0" class="flex items-center gap-1">
              <UiIcon name="star" class="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              <UiText as="span" variant="caption-normal" class="font-semibold text-gray-800">
                {{ expert.average_rating?.toFixed(1) }}
              </UiText>
              <UiText as="span" variant="caption-normal" class="text-gray-500">
                ({{ expert.reviews_count }})
              </UiText>
            </div>

            <div v-if="priceDisplay" class="flex items-center gap-1">
              <UiIcon name="dollar-circle" class="w-3.5 h-3.5 text-gray-400" />
              <UiText as="span" variant="caption-normal" class="text-gray-600">
                From <span class="font-medium text-gray-800">{{ priceDisplay }}</span>
              </UiText>
            </div>

            <div v-if="expert.languages?.length" class="flex items-center gap-1">
              <UiIcon name="map-pin" class="w-3.5 h-3.5 text-gray-400" />
              <UiText as="span" variant="caption-normal" class="text-gray-500">
                {{ expert.languages.slice(0, 3).join(', ') }}
                <span v-if="expert.languages.length > 3" class="text-gray-400">
                  +{{ expert.languages.length - 3 }}
                </span>
              </UiText>
            </div>

            <div class="flex items-center gap-1">
              <UiIcon name="calendar-color" class="w-3.5 h-3.5 text-gray-400" />
              <UiText as="span" variant="caption-normal" class="text-gray-500">
                Joined {{ joinedDate }}
              </UiText>
            </div>

            <div v-if="expert.is_claimed === false" class="flex items-center gap-1">
              <span class="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
              <UiText as="span" variant="caption-normal" class="text-orange-600">
                Unclaimed
              </UiText>
            </div>

            <div v-if="expert.notes_count > 0" class="flex items-center gap-1">
              <UiIcon name="pencil" class="w-3.5 h-3.5 text-gray-400" />
              <UiText as="span" variant="caption-normal" class="text-gray-500">
                {{ expert.notes_count }} note{{ expert.notes_count !== 1 ? 's' : '' }}
              </UiText>
            </div>
          </div>

          <!-- Internal tags -->
          <div v-if="expert.tags.length > 0" class="flex flex-wrap items-center gap-1.5 mt-2.5">
            <span
              v-for="tag in expert.tags"
              :key="tag.id"
              class="inline-flex items-center rounded-md border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
            >
              {{ tag.name }}
            </span>
          </div>

          <!-- Services — micro tag clusters with vertical accent -->
          <div v-if="expert.offered_services.length > 0" class="mt-3.5 flex flex-wrap gap-x-5 gap-y-2">
            <div
              v-for="service in expert.offered_services"
              :key="service.service_category.id"
              class="flex gap-1.5 min-w-0"
            >
              <div class="w-px rounded-full bg-gray-200 shrink-0 self-stretch" />
              <div class="min-w-0">
                <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 leading-none">{{ service.service_category.name }}</span>
                <p v-if="service.sub_categories.length > 0" class="text-[11px] text-gray-400 leading-snug mt-px">
                  {{ service.sub_categories.map(s => s.name).join(', ') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Badges -->
          <div v-if="expert.badges.length > 0" class="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <PartnerChip
              v-for="badge in expert.badges.slice(0, 3)"
              :key="badge.id"
              :name="badge.name"
              :icon-url="getBadgeIconUrl(badge)"
              size="xs"
              rounded="small"
            />
            <span
              v-if="expert.badges.length > 3"
              class="text-[11px] text-gray-400"
            >
              +{{ expert.badges.length - 3 }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
